/**
 * ErrorBoundary - 错误边界组件
 */
import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { errorTracker } from '@/lib/error-tracking';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    errorTracker.captureException(error, {
      level: 'error',
      extra: {
        componentStack: errorInfo.componentStack ?? '',
      },
    });
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = () => {
    errorTracker.addBreadcrumb({
      type: 'ui',
      message: 'ErrorBoundary reset',
    });
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div className="flex flex-col items-center justify-center p-8">
            <AlertCircle className="mb-4 h-12 w-12 text-error" />
            <h2 className="text-lg font-medium">出错了</h2>
            <p className="mt-1 text-sm text-text-secondary">
              {this.state.error?.message || '发生未知错误'}
            </p>
            <Button className="mt-4" onClick={this.handleReset}>
              重试
            </Button>
          </div>
        )
      );
    }

    return this.props.children;
  }
}
